import React, { useState, useEffect } from "react";
import { useToast } from "../../../Toaster/ToastContext";
import PropTypes from "prop-types";
import { apiClient } from "../../../services/apiClient";
import { PlusCircle } from "feather-icons-react/build/IconComponents";

const imageStyles = {
  imagePreviewContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  removeImageBtn: {
    position: "absolute",
    top: "5px",
    right: "5px",
    background: "rgba(255, 255, 255, 0.8)",
    border: "none",
    borderRadius: "50%",
    width: "25px",
    height: "25px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "18px",
    color: "#dc3545",
    padding: 0,
  },
};

export default function AddCategoryList({ category, onSave }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    status: "Active",
    image: "",
  });
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
    try {
      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);
      if (getPermissions.permissions) {
        parsedPermissions = getPermissions.permissions;
      } else {
        parsedPermissions = getPermissions;
      }

      const inventoryPermissions = parsedPermissions.find(
        (item) => item.title === "Inventory"
      );

      const productPermissions = inventoryPermissions?.childPage?.find(
        (child) => child.title === "Brands"
      );
      // console.log("search product", productPermissions)

      if (productPermissions) {
        setPermissions({
          create: productPermissions.create || false,
          read: productPermissions.read || false,
          update: productPermissions.update || false,
          delete: productPermissions.delete || false,
        });
      } else {
        console.error("Product permissions not found in localStorage");
        showToast(
          "Product permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  useEffect(() => {
    if (category) {
      setFormData({
        id: category.id,
        name: category.name || "",
        slug: category.slug || "",
        status: category.status || "Active",
        image: category.image || "",
      });
      setImagePreview(category.image || null);
    } else {
      setFormData({ name: "", slug: "", status: "Active", image: "" });
      setImagePreview(null);
      setImageFile(null);
    }
    setErrors({ name: "", slug: "", status: "", image: "" });
  }, [category]);

  const [errors, setErrors] = useState({
    name: "",
    slug: "",
    status: "",
    image: "",
  });

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        return value.trim() ? "" : "Brand Name is required";
      case "slug":
        return value.trim() ? "" : "Brand Slug is required";
      case "status":
        return value ? "" : "Status is required";
      case "image":
        return value || imageFile ? "" : "Image is required";
      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({ ...prev, image: reader.result }));
        setErrors((prev) => ({ ...prev, image: "" }));
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const generateRandomSlug = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const slugLength = 8;
    let result = "";
    for (let i = 0; i < slugLength; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    setFormData((prev) => ({ ...prev, slug: result }));
    setErrors((prev) => ({ ...prev, slug: validateField("slug", result) }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField("name", formData.name),
      slug: validateField("slug", formData.slug),
      status: validateField("status", formData.status),
      image: validateField("image", formData.image),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile && (!formData.image || formData.image.trim() === "")) {
      setErrors((prev) => ({ ...prev, image: "Image is required" }));
      return;
    }

    if (validateForm()) {
      setLoading(true);
      try {
        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.name);
        formDataToSend.append("slug", formData.slug);
        formDataToSend.append("status", formData.status);

        if (imageFile) {
          formDataToSend.append("image", imageFile);
        } else if (
          formData.image &&
          typeof formData.image === "string" &&
          formData.image.trim() !== ""
        ) {
          formDataToSend.append("image", formData.image);
        }

        if (formData.id) {
          formDataToSend.append("id", formData.id);
        }

        const response = await apiClient.post(
          "/store-category",
          formDataToSend,
          true
        );
        const Apidata = await response;
        if (Apidata.success) {
          setFormData({ name: "", slug: "", status: "Active", image: "" });
          setImagePreview(null);
          setImageFile(null);
          setErrors({ name: "", slug: "", status: "", image: "" });
          document
            .getElementById("add-category")
            .querySelector('[data-bs-dismiss="modal"]')
            .click();

          showToast(Apidata.message, "success", "Brand Operation!");
          onSave && onSave();
        } else {
          showToast(
            Apidata.message || "Please contact the support team.",
            "error",
            "Brand Operation!"
          );
        }
      } catch (error) {
        showToast(
          error.message ||
            "An unexpected error occurred: Please contact the support team.",
          "error",
          "Brand Operation!"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <div className="modal fade" id="add-category">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                  <div className="page-title">
                    <h4>{category ? "Update Brand" : "Create Brand"}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Image</label>
                      <div className="profile-pic-upload mb-3">
                        <div
                          className={`profile-pic brand-pic ${
                            errors.image ? "is-invalid" : ""
                          }`}
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "120px",
                            border: "1px dashed #ccc",
                            borderRadius: "8px",
                            cursor: "pointer",
                            overflow: "hidden",
                          }}
                        >
                          {imagePreview ? (
                            <div
                              style={{
                                ...imageStyles.imagePreviewContainer,
                                position: "relative",
                                width: "100%",
                                height: "100%",
                              }}
                            >
                              <img
                                src={imagePreview}
                                alt="Category"
                                style={imageStyles.previewImage}
                              />

                              <button
                                type="button"
                                style={imageStyles.removeImageBtn}
                                onClick={() => {
                                  handleRemoveImage();

                                  document.getElementById(
                                    "category-image-upload"
                                  ).value = "";
                                }}
                              >
                                ×
                              </button>
                              <label
                                htmlFor="category-image-upload"
                                style={{
                                  position: "absolute",
                                  bottom: "5px",
                                  right: "5px",
                                  background: "#ff9f43",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: "28px",
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                }}
                              >
                                ✎
                              </label>
                            </div>
                          ) : (
                            <label
                              htmlFor="category-image-upload"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                cursor: "pointer",
                                color: "#666",
                                fontSize: "14px",
                                textAlign: "center",
                              }}
                            >
                              <PlusCircle
                                className="plus-down-add"
                                size={28}
                                style={{ marginBottom: "6px" }}
                              />
                              Add Image
                            </label>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          id="category-image-upload"
                          style={{ display: "none" }}
                        />
                      </div>
                      {errors.image && (
                        <div className="invalid-feedback d-block">
                          {errors.image}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Brand</label>
                      <input
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Brand Slug</label>
                      <div className="d-flex align-items-center">
                        <input
                          type="text"
                          className={`form-control ${
                            errors.slug ? "is-invalid" : ""
                          } me-2`}
                          name="slug"
                          value={formData.slug}
                          onChange={handleInputChange}
                          placeholder="Enter category slug"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary fs-13 fw-medium p-2 px-3"
                          onClick={generateRandomSlug}
                        >
                          Random
                        </button>
                      </div>
                      {errors.slug && (
                        <div className="invalid-feedback">{errors.slug}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className={`form-select ${
                          errors.status ? "is-invalid" : ""
                        }`}
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {errors.status && (
                        <div className="invalid-feedback">{errors.status}</div>
                      )}
                    </div>

                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      {(category ? permissions.update : permissions.create) && (
                        <button
                          type="submit"
                          className="btn btn-submit"
                          disabled={loading}
                        >
                          {loading
                            ? "Processing..."
                            : category
                            ? "Update Brand"
                            : "Create Brand"}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Category */}
    </div>
  );
}

AddCategoryList.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    status: PropTypes.oneOf(["active", "inactive", ""]),
    image: PropTypes.string,
  }),
  onSave: PropTypes.func,
};

AddCategoryList.defaultProps = {
  category: null,
};
